import type { Theme } from "@mui/material/styles";
import { alpha, styled } from "@mui/material/styles";
import { Handle } from "@xyflow/react";

/** Light teal for legacy outer solid + inner dashed rings (no longer rendered
 *  on the visible BaseHandle — kept for BaseHiddenHandle / EdgeAddButton). */
export const HANDLE_BORDER_TEAL = "#95eddc";

export const HANDLE_SIZE = 10;

const outerBorderPx = 1.5;
const dashBorderPx = 1.5;
/** Inset dashed frame — sized to sit evenly inside the outer ring. */
const innerFramePx = 28;
const outerRadiusPx = 6;
const innerRadiusPx = 4;

const outerChrome = (theme: Theme) => ({
	border: `${outerBorderPx}px solid ${HANDLE_BORDER_TEAL}`,
	backgroundColor: theme.palette.common.white,
	borderRadius: outerRadiusPx,
	boxSizing: "border-box" as const,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	lineHeight: 0,
});

// Bottom-of-node *connection* dot. Visually distinct from the bottom-right
// corner "+" button: this one is OUTLINED (white interior, teal ring), and
// the cursor is "crosshair" so users read it as a draggable connection
// point rather than a button. The "+" corner button is solid filled and
// reads as a button.
export const BaseHandle = styled(Handle)(() => ({
	pointerEvents: "all",
	cursor: "crosshair !important" as any,
	width: HANDLE_SIZE,
	height: HANDLE_SIZE,
	minWidth: HANDLE_SIZE,
	minHeight: HANDLE_SIZE,
	background: "#FFFFFF",
	border: "2px solid #02D5AB",
	borderRadius: "50%",
	boxShadow: "none",
	transition: "transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
	"&:hover": {
		background: "#02D5AB",
		transform: "translate(-50%, 0) scale(1.4)",
		boxShadow: "0 0 0 4px rgba(2, 213, 171, 0.25)",
	},
}));

export const ADD_HANDLE_SIZE = 24;
// React Flow applies translate(-50%, -50%) to bottom handles, which lifts the
// element 12px UP. To fully float the 24px button outside the tile with a
// ~4px gap, we need bottom: -40 so the visual top edge lands just below the
// node's bottom border: nodeBottom + (-40) - 24 - (-12) = nodeBottom + 4.
export const ADD_HANDLE_FLOAT_OFFSET = 40;
export const AddNextHandle = styled(Handle)(() => ({
	pointerEvents: "all",
	cursor: "pointer !important" as any,
	width: ADD_HANDLE_SIZE,
	height: ADD_HANDLE_SIZE,
	minWidth: ADD_HANDLE_SIZE,
	minHeight: ADD_HANDLE_SIZE,
	background: "#FFFFFF",
	border: `1.5px solid ${HANDLE_BORDER_TEAL}`,
	borderRadius: "50%",
	boxShadow: `0 0 0 4px rgba(149, 237, 220, 0.18), 0 1px 3px rgba(13, 31, 48, 0.08)`,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	transition: "box-shadow 0.18s ease, transform 0.18s ease, background 0.18s ease",
	// Float the button below the node (bottom handle only). `&&` raises
	// specificity above React Flow's `.react-flow__handle-bottom` rule.
	"&&.react-flow__handle-bottom": {
		bottom: -ADD_HANDLE_FLOAT_OFFSET,
	},
	"&:hover": {
		boxShadow: `0 0 0 6px rgba(149, 237, 220, 0.32), 0 2px 6px rgba(13, 31, 48, 0.12)`,
		transform: "translate(-50%, 0) scale(1.08)",
	},
	"&:active": {
		transform: "translate(-50%, 0) scale(0.96)",
	},
}));

/** Inner dashed rounded square; plus SVG is centered inside via flex. */
export const AddHandleInnerFrame = styled("div")({
	boxSizing: "border-box",
	width: innerFramePx,
	height: innerFramePx,
	flexShrink: 0,
	border: `${dashBorderPx}px dashed ${HANDLE_BORDER_TEAL}`,
	borderRadius: innerRadiusPx,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	lineHeight: 0,
});

export const EdgeAddButton = styled("div")(({ theme }) => ({
	pointerEvents: "all",
	cursor: "pointer",
	width: 24,
	height: 24,
	...outerChrome(theme),
	borderRadius: "50%",
	transition: "box-shadow 0.18s ease, transform 0.18s ease",
	boxShadow: `0 0 0 4px ${alpha(HANDLE_BORDER_TEAL, 0.18)}, 0 1px 3px rgba(13, 31, 48, 0.08)`,
	"&:hover": {
		boxShadow: `0 0 0 6px ${alpha(HANDLE_BORDER_TEAL, 0.32)}, 0 2px 6px rgba(13, 31, 48, 0.12)`,
		transform: "scale(1.08)",
	},
	"&:active": {
		transform: "scale(0.96)",
	},
}));

export const BaseHiddenHandle = styled(Handle)({
	width: 14,
	height: 14,
	background: "#DBDCDE",
	border: "2px solid #fff",
	borderRadius: 2,
	opacity: 0,
	pointerEvents: "all",
	transition: "opacity 0.15s ease, transform 0.15s ease",
	"&:hover, &.connectingto, &.valid": {
		opacity: 1,
	},
});

/** Anchor for edges when the visible + handle is hidden; stays invisible (no hover reveal). */
export const DecisionHiddenHandle = styled(Handle)({
	width: 14,
	height: 14,
	background: "#DBDCDE",
	border: "2px solid #fff",
	borderRadius: 2,
	opacity: 0,
	pointerEvents: "all",
});
